/**
 * Shared styled components for ScoreCard and MaxiScoreCard.
 * Extracted to eliminate ~80% duplication between the two scorecard variants.
 */
import styled, { keyframes, css } from 'styled-components';
import { scoreHighlightAnimation } from '../../utils/animationUtils';

// Keyframe animations
export const highlightPop = keyframes`0%{transform:scale(.96);}40%{transform:scale(1.03);}100%{transform:scale(1);}`;
export const bluePulse = keyframes`0%{box-shadow:0 0 0 0 rgba(33,150,243,.55);}70%{box-shadow:0 0 0 10px rgba(33,150,243,0);}100%{box-shadow:0 0 0 0 rgba(33,150,243,0);}`;
export const subtleGlow = keyframes`0%{opacity:.6;}50%{opacity:1;}100%{opacity:.6;}`;

export const ScoreCardContainer = styled.div`
  /* Felt-style dynamic background using theme-derived CSS vars */
  --felt-base: var(--felt-base-color, #0f6d2f);
  --felt-dark: var(--felt-dark-color, #094921);
  --felt-light: var(--felt-light-color, #158a3c);
  --felt-edge: var(--felt-edge-color, #062d15);
  position: relative;
  border-radius: 14px;
  padding: 22px 22px 20px;
  min-width: 300px;
  width: 100%;
  max-width: 540px;
  background:
    radial-gradient(circle at 30% 28%, rgba(255,255,255,0.08), transparent 55%),
    radial-gradient(circle at 72% 65%, rgba(255,255,255,0.06), transparent 60%),
    linear-gradient(140deg, var(--felt-dark) 0%, var(--felt-base) 55%, var(--felt-light) 100%);
  box-shadow:
    inset 0 1px 3px rgba(255,255,255,0.15),
    inset 0 -1px 4px rgba(0,0,0,0.25),
    0 6px 18px -4px rgba(0,0,0,0.35),
    0 2px 6px rgba(0,0,0,0.18);
  border: 2px solid var(--felt-edge);
  outline: 1px solid rgba(255,255,255,0.08);
  overflow: hidden;
  color: #fff;

  &::before, &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
  }
  /* Subtle fiber/noise texture */
  &::before {
    background: repeating-linear-gradient(45deg, rgba(255,255,255,0.04) 0 2px, transparent 2px 4px),
                repeating-linear-gradient(-45deg, rgba(0,0,0,0.04) 0 3px, transparent 3px 6px);
    mix-blend-mode: overlay;
    opacity: 0.35;
  }
  /* Vignette/edge darkening */
  &::after {
    background: radial-gradient(circle at center, rgba(0,0,0,0) 55%, rgba(0,0,0,0.35) 100%);
    opacity: 0.55;
  }

  /* Smart scrolling for tall scoreboards */
  @media (min-width: 768px) and (max-height: 1000px) {
    max-height: min(calc(100vh - 160px), 720px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-track { background: rgba(0,0,0,0.1); border-radius: 4px; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.3); border-radius: 4px; }
    &::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.5); }
  }

  @media (min-width: 768px) and (orientation: landscape) and (max-height: 600px) {
    max-height: calc(100vh - 120px);
    padding: 20px 20px 18px;
  }

  @media (min-width: 1400px) {
    max-width: 680px;
    padding: 30px 30px 28px;
    border-radius: 18px;
    border: 3px solid var(--felt-edge);
  }

  @media (min-width: 1920px) {
    max-width: 800px;
    padding: 36px 36px 32px;
    border-radius: 22px;
    border: 4px solid var(--felt-edge);
  }

  @media (max-width: 768px) {
    padding: 16px 16px 14px;
    border-radius: 12px;
  }
`;

export const SectionTitle = styled.h3`
  margin: 0 0 15px 0;
  color: var(--scorecard-heading-color, #fff);
  font-size: 18px;
  border-bottom: 2px solid rgba(255,255,255,0.15);
  padding-bottom: 8px;
`;

export const ScoreRow = styled.div<{
  $isSelectable: boolean;
  $isSelected: boolean;
  $isStruck?: boolean;
  $isCompleted?: boolean;
  $highlight?: boolean;
  $shouldShowHand?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 6px;
  position: relative;
  background: ${p => {
    if (p.$isStruck) return 'rgba(255,110,64,0.15)';
    if (p.$isCompleted) return 'rgba(76,175,80,0.15)';
    if (p.$shouldShowHand) return 'rgba(33,150,243,0.22)';
    if (p.$isSelectable) return 'transparent';
    return 'transparent';
  }};
  cursor: ${props => props.$isSelectable ? 'pointer' : 'default'};
  border: 2px solid ${p =>
    p.$isStruck ? 'rgba(255,110,64,0.55)' :
    p.$isCompleted ? 'rgba(76,175,80,0.55)' :
    p.$shouldShowHand ? 'rgba(33,150,243,1)' :
    'rgba(255,255,255,0.15)'};
  box-shadow: ${p => p.$shouldShowHand ? '0 0 12px rgba(33,150,243,0.5), inset 0 0 20px rgba(33,150,243,0.15)' : 'none'};
  transition: all 0.3s ease;
  ${(p) => p.$highlight && css`animation:${highlightPop} .5s ease; box-shadow:0 0 0 3px rgba(76,175,80,.4);`}
  ${(p) => p.$shouldShowHand && css`animation:${bluePulse} 1.8s ease-out infinite;`}
  ${(p) => p.$isStruck && css`&::after{content:'0';position:absolute;right:8px;top:50%;transform:translateY(-50%);font-size:10px;color:rgba(255,255,255,0.4);}`}

  &:hover {
    background: ${props => props.$shouldShowHand ? 'rgba(33,150,243,0.35)' : props.$isSelectable ? 'rgba(255,255,255,0.05)' : 'transparent'};
    transform: ${props => props.$shouldShowHand ? 'scale(1.02)' : 'none'};
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
  }
`;

export const CategoryInfo = styled.div`
  flex: 1;
`;

export const StatusIcon = styled.span<{ $variant: 'star' | 'check' | 'cross' }>`
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: ${p => p.$variant === 'star' ? '18px' : '15px'};
  font-weight: 700;
  width: auto;
  color: ${p => p.$variant === 'cross' ? '#ff7043' : p.$variant === 'star' ? '#ffc107' : '#4caf50'};
  text-shadow: 0 1px 2px rgba(0,0,0,.55);
  filter: drop-shadow(0 2px 2px rgba(0,0,0,.45));
`;

export const CategoryName = styled.div`
  font-weight: 600;
  color: var(--scorecard-text, #fff);
  font-size: 14px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.5);
  @media (max-width: 480px) {
    font-size: 15px;
  }
`;

export const CategoryDescription = styled.div`
  font-size: 12px;
  color: var(--scorecard-muted, rgba(255,255,255,0.75));
  margin-top: 2px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

export const ScoreValue = styled.div<{ $isPossible: boolean; $dim?: boolean; $highlight?: boolean }>`
  font-weight: 700;
  animation: ${props => props.$highlight ? `${scoreHighlightAnimation} 0.8s ease` : 'none'};
  position: relative;
  z-index: 2;
  font-size: 16px;
  min-width: 40px;
  text-align: right;
  color: ${props => props.$isPossible ? 'var(--scorecard-possible, #90e7ff)' : 'var(--scorecard-text, #fff)'};
  opacity: ${props => props.$dim ? 0.55 : 1};
  font-style: ${props => props.$dim ? 'italic' : 'normal'};
  text-shadow: 0 1px 2px rgba(0,0,0,0.55);
  letter-spacing: .5px;
  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin: 8px 0;
  font-weight: 600;
  border-top: 2px solid rgba(255,255,255,0.15);
  color: var(--scorecard-text, #fff);
  text-shadow: 0 1px 2px rgba(0,0,0,0.4);
`;

export const TotalScore = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  text-align: center;
  padding: 18px 16px 16px;
  background: linear-gradient(145deg, rgba(255,255,255,0.12), rgba(255,255,255,0.05));
  border: 1px solid rgba(255,255,255,0.25);
  box-shadow: inset 0 1px 2px rgba(255,255,255,0.2), 0 4px 10px -2px rgba(0,0,0,0.5);
  border-radius: 10px;
  margin-top: 16px;
  letter-spacing: .7px;
  text-shadow: 0 2px 4px rgba(0,0,0,0.5);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;
